import type React from 'react';
import { useRef, useState } from 'react';
import { isEqual, isFunction } from '../utils.js';

interface ShouldUpdateArg {
  prop: unknown;
  prevProp: unknown;
  stateValue: unknown;
}

type OnUpdateT<T> = (prop: T, prevProp?: T | Record<string, never>) => T;

interface OptionsT<T> {
  onUpdate?: OnUpdateT<T>;
  shouldUpdate?: (arg: ShouldUpdateArg) => boolean;
  updateOnStateChange?: boolean;
}

const defaultPropUpdateOnChange = <T,>(prop: T) => prop;

function defaultShouldUpdate({ prop, prevProp, stateValue }: ShouldUpdateArg) {
  return !isEqual(prop, prevProp) && !isEqual(prop, stateValue);
}

function setStateWithUpdate<T>(onUpdate: OnUpdateT<T>, setState: React.Dispatch<React.SetStateAction<T>>) {
  return (state: React.SetStateAction<T>) => {
    setState((prevState: T) => onUpdate(isFunction(state) ? state(prevState) : state, prevState));
  };
}

export const useDerivedStateFromProps = <T,>(
  prop: T,
  options: OptionsT<T> = {},
): [T, React.Dispatch<React.SetStateAction<T>>] => {
  const {
    onUpdate = defaultPropUpdateOnChange as OnUpdateT<T>,
    shouldUpdate = defaultShouldUpdate,
    updateOnStateChange = false,
  } = options;

  const [stateValue, setState] = useState(onUpdate(prop, {}));
  const handleSetState = updateOnStateChange ? setStateWithUpdate(onUpdate, setState) : setState;
  const prevProp = useRef(prop);

  if (
    shouldUpdate({
      prop,
      prevProp: prevProp.current,
      stateValue,
    })
  ) {
    const nextProp = onUpdate(prop, prevProp.current);
    setState(nextProp);
  }
  prevProp.current = prop;
  return [stateValue, handleSetState];
};

export default useDerivedStateFromProps;
